import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import webhookService, {
  CreateWebhookConfigRequest,
  UpdateWebhookConfigRequest,
  WebhookConfig,
  WebhookEventCatalogItem,
  WebhookFilterField,
  WebhookFilterRule,
  WebhookFilters,
  WebhookLog,
  WebhookStats,
  WebhookSyncJob,
} from '../../services/webhookService';
import { argentinaDayEndIso, argentinaDayStartIso, formatDateTimeAr } from '../../utils/formatters';

type FormState = {
  nombre: string;
  url: string;
  descripcion: string;
  secret: string;
  clearSecret: boolean;
  activo: boolean;
  eventos: string[];
  maxReintentos: number;
  timeoutMs: number;
  headersText: string;
  emailAlertaFallos: string;
  filtrosMatch: 'all' | 'any';
  filtrosRules: WebhookFilterRule[];
};

const emptyForm = (): FormState => ({
  nombre: '',
  url: '',
  descripcion: '',
  secret: '',
  clearSecret: false,
  activo: true,
  eventos: [],
  maxReintentos: 3,
  timeoutMs: 30000,
  headersText: '',
  emailAlertaFallos: '',
  filtrosMatch: 'all',
  filtrosRules: [],
});

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' | 'info' => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    case 'retrying':
    case 'pending':
      return 'warning';
    case 'filtered_out':
      return 'info';
    default:
      return 'default';
  }
};

const Webhooks: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<WebhookConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<WebhookEventCatalogItem[]>([]);
  const [filterFields, setFilterFields] = useState<WebhookFilterField[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [formTab, setFormTab] = useState(0);
  const [editing, setEditing] = useState<WebhookConfig | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState<WebhookConfig | null>(null);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDesde, setLogsDesde] = useState('');
  const [logsHasta, setLogsHasta] = useState('');

  const [syncOpen, setSyncOpen] = useState(false);
  const [syncConfigId, setSyncConfigId] = useState<string | null>(null);
  const [syncJob, setSyncJob] = useState<WebhookSyncJob | null>(null);
  const [syncOpts, setSyncOpts] = useState({
    soloActivos: true,
    omitirYaSincronizados: true,
    dryRun: false,
  });

  const eventsByGroup = useMemo(() => {
    const map = new Map<string, WebhookEventCatalogItem[]>();
    events.forEach((e) => {
      const list = map.get(e.group) ?? [];
      list.push(e);
      map.set(e.group, list);
    });
    return Array.from(map.entries());
  }, [events]);

  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await webhookService.listConfigs(page + 1, limit);
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error al cargar webhooks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, limit, page]);

  useEffect(() => {
    void loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    void (async () => {
      try {
        const [ev, ff] = await Promise.all([
          webhookService.getEvents(),
          webhookService.getFilterFields(),
        ]);
        setEvents(ev);
        setFilterFields(ff);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormTab(0);
    setFormOpen(true);
  };

  const openEdit = (row: WebhookConfig) => {
    setEditing(row);
    setForm({
      nombre: row.nombre ?? '',
      url: row.url,
      descripcion: row.descripcion ?? '',
      secret: '',
      clearSecret: false,
      activo: row.activo,
      eventos: [...row.eventos],
      maxReintentos: row.maxReintentos,
      timeoutMs: row.timeoutMs,
      headersText: row.headers ? JSON.stringify(row.headers, null, 2) : '',
      emailAlertaFallos: row.emailAlertaFallos ?? '',
      filtrosMatch: row.filtros?.match === 'any' ? 'any' : 'all',
      filtrosRules: row.filtros?.rules ? [...row.filtros.rules] : [],
    });
    setFormTab(0);
    setFormOpen(true);
  };

  const parseHeaders = (): Record<string, string> | null => {
    if (!form.headersText.trim()) return null;
    try {
      const parsed = JSON.parse(form.headersText) as Record<string, string>;
      if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('invalid');
      return parsed;
    } catch {
      throw new Error('Headers debe ser un objeto JSON v�lido');
    }
  };

  const buildFiltros = (): WebhookFilters | null => {
    if (!form.filtrosRules.length) return null;
    return { match: form.filtrosMatch, rules: form.filtrosRules };
  };

  const handleSave = async () => {
    if (!form.url.trim() || !form.eventos.length) {
      enqueueSnackbar('URL y al menos un evento son obligatorios', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const headers = parseHeaders();
      const filtros = buildFiltros();
      if (editing) {
        const body: UpdateWebhookConfigRequest = {
          url: form.url.trim(),
          nombre: form.nombre.trim() || null,
          descripcion: form.descripcion.trim() || null,
          eventos: form.eventos,
          activo: form.activo,
          maxReintentos: form.maxReintentos,
          timeoutMs: form.timeoutMs,
          headers,
          emailAlertaFallos: form.emailAlertaFallos.trim() || null,
          clearEmailAlertaFallos: !form.emailAlertaFallos.trim(),
          filtros,
          clearFiltros: !filtros,
          clearSecret: form.clearSecret,
        };
        if (form.secret.trim()) body.secret = form.secret.trim();
        await webhookService.updateConfig(editing.id, body);
        enqueueSnackbar('Webhook actualizado', { variant: 'success' });
      } else {
        const body: CreateWebhookConfigRequest = {
          url: form.url.trim(),
          nombre: form.nombre.trim() || null,
          descripcion: form.descripcion.trim() || null,
          secret: form.secret.trim() || null,
          eventos: form.eventos,
          activo: form.activo,
          maxReintentos: form.maxReintentos,
          timeoutMs: form.timeoutMs,
          headers,
          emailAlertaFallos: form.emailAlertaFallos.trim() || null,
          filtros,
        };
        await webhookService.createConfig(body);
        enqueueSnackbar('Webhook creado', { variant: 'success' });
      }
      setFormOpen(false);
      await loadConfigs();
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (e as { message?: string })?.message
        || 'Error al guardar webhook';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: WebhookConfig) => {
    if (!window.confirm(`�Eliminar webhook "${row.nombre || row.url}"?`)) return;
    try {
      await webhookService.deleteConfig(row.id);
      enqueueSnackbar('Webhook eliminado', { variant: 'success' });
      if (detail?.id === row.id) setDetail(null);
      await loadConfigs();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error al eliminar', { variant: 'error' });
    }
  };

  const loadDetail = async (
    row: WebhookConfig,
    logsPageNum = 0,
    desde = logsDesde,
    hasta = logsHasta,
  ) => {
    if (desde && hasta && desde > hasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }
    setDetail(row);
    setLogsPage(logsPageNum);
    setLogsLoading(true);
    const query = {
      fechaDesde: desde ? argentinaDayStartIso(desde) : undefined,
      fechaHasta: hasta ? argentinaDayEndIso(hasta) : undefined,
    };
    try {
      const [st, lg] = await Promise.all([
        webhookService.getStats(row.id, query),
        webhookService.getLogs(row.id, logsPageNum + 1, 25, query),
      ]);
      setStats(st);
      setLogs(lg.items);
      setLogsTotal(lg.total);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error al cargar detalle', { variant: 'error' });
    } finally {
      setLogsLoading(false);
    }
  };

  const openDetail = (row: WebhookConfig) => {
    setLogsDesde('');
    setLogsHasta('');
    void loadDetail(row, 0, '', '');
  };

  const handleTest = async (row: WebhookConfig) => {
    try {
      await webhookService.testConfig(row.id);
      enqueueSnackbar('Prueba enviada', { variant: 'success' });
      if (detail?.id === row.id) await loadDetail(row, logsPage);
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Error al probar webhook';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleRetryLog = async (log: WebhookLog) => {
    if (!detail) return;
    try {
      await webhookService.retryLog(detail.id, log.id);
      enqueueSnackbar('Reintento iniciado', { variant: 'info' });
      await loadDetail(detail, logsPage);
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'No se pudo reintentar';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const startSync = async () => {
    if (!syncConfigId) return;
    try {
      const job = await webhookService.syncClientes({
        webhookConfigId: syncConfigId,
        ...syncOpts,
      });
      setSyncJob(job);
      if (syncOpts.dryRun) {
        enqueueSnackbar(`Dry-run: ${job.enviados} potenciales env�os`, { variant: 'info' });
        return;
      }
      enqueueSnackbar('Sincronizaci�n iniciada', { variant: 'success' });
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Error al iniciar sync';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (!syncJob || syncOpts.dryRun) return;
    if (['completed', 'failed', 'cancelled'].includes(syncJob.status)) return;
    const t = window.setInterval(async () => {
      try {
        const job = await webhookService.getSyncJob(syncJob.id);
        setSyncJob(job);
      } catch {
        /* ignore */
      }
    }, 1500);
    return () => window.clearInterval(t);
  }, [syncJob, syncOpts.dryRun]);

  const toggleEvent = (ev: string) => {
    setForm((prev) => ({
      ...prev,
      eventos: prev.eventos.includes(ev)
        ? prev.eventos.filter((x) => x !== ev)
        : [...prev.eventos, ev],
    }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f3f4f6', minHeight: '100vh' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Webhooks</Typography>
          <Typography color="text.secondary">
            Notificaciones HTTP outbound a sistemas externos (HMAC, reintentos y logs).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={() => navigate('/configuraciones/webhooks/ayuda')}>
            Documentación
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={() => void loadConfigs()}>Actualizar</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nuevo webhook</Button>
        </Stack>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <Alert severity="info">No hay webhooks configurados.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>URL</TableCell>
                      <TableCell>Eventos</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.nombre || '�'}</TableCell>
                        <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.url}</TableCell>
                        <TableCell>{row.eventos.length}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.activo ? 'Activo' : 'Inactivo'} color={row.activo ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          <TooltipTitle title="Ver">
                            <IconButton size="small" onClick={() => openDetail(row)}><VisibilityIcon fontSize="small" /></IconButton>
                          </TooltipTitle>
                          <TooltipTitle title="Editar">
                            <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                          </TooltipTitle>
                          <TooltipTitle title="Probar">
                            <IconButton size="small" onClick={() => void handleTest(row)}><PlayArrowIcon fontSize="small" /></IconButton>
                          </TooltipTitle>
                          {row.eventos.includes('cliente.created') && (
                            <TooltipTitle title="Sync clientes">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSyncConfigId(row.id);
                                  setSyncJob(null);
                                  setSyncOpen(true);
                                }}
                              >
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </TooltipTitle>
                          )}
                          <TooltipTitle title="Eliminar">
                            <IconButton size="small" color="error" onClick={() => void handleDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                          </TooltipTitle>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Filas"
              />
            </>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Detalle: {detail.nombre || detail.url}</Typography>
              <Button onClick={() => void loadDetail(detail, logsPage)} startIcon={<RefreshIcon />}>Actualizar logs</Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
              <TextField
                label="Desde"
                type="date"
                size="small"
                value={logsDesde}
                onChange={(e) => {
                  const v = e.target.value;
                  setLogsDesde(v);
                  void loadDetail(detail, 0, v, logsHasta);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                value={logsHasta}
                onChange={(e) => {
                  const v = e.target.value;
                  setLogsHasta(v);
                  void loadDetail(detail, 0, logsDesde, v);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={logsDesde ? { min: logsDesde } : undefined}
              />
            </Stack>
            {stats && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip label={`Total ${stats.total}`} />
                <Chip color="success" label={`Éxitos ${stats.exitosos}`} />
                <Chip color="error" label={`Fallidos ${stats.fallidos}`} />
                <Chip color="warning" label={`Pendientes ${stats.pendientes}`} />
                <Chip label={`Tasa ${stats.tasaExito}%`} />
              </Stack>
            )}
            {logsLoading ? <CircularProgress size={28} /> : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Evento</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Intentos</TableCell>
                        <TableCell>HTTP</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((log) => (
                        <React.Fragment key={log.id}>
                          <TableRow>
                            <TableCell>{formatDateTimeAr(log.fechaCreacion)}</TableCell>
                            <TableCell>{log.evento}</TableCell>
                            <TableCell><Chip size="small" label={log.status} color={statusColor(log.status)} /></TableCell>
                            <TableCell>{log.intentos}</TableCell>
                            <TableCell>{log.statusCode ?? '�'}</TableCell>
                            <TableCell>
                              {log.status === 'failed' && (
                                <Button size="small" onClick={() => void handleRetryLog(log)}>Reintentar</Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {(log.error || log.respuesta) && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography variant="caption" color="error" component="div">{log.error}</Typography>
                                {log.respuesta && (
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                                    {log.respuesta}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={logsTotal}
                  page={logsPage}
                  onPageChange={(_, p) => { if (detail) void loadDetail(detail, p); }}
                  rowsPerPage={25}
                  rowsPerPageOptions={[25]}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar webhook' : 'Nuevo webhook'}</DialogTitle>
        <DialogContent dividers>
          <Tabs value={formTab} onChange={(_, v) => setFormTab(v)} sx={{ mb: 2 }}>
            <Tab label="Conexi�n" />
            <Tab label="Eventos" />
            <Tab label="Filtros" />
            <Tab label="M�s opciones" />
          </Tabs>

          {formTab === 0 && (
            <Stack spacing={2}>
              <TextField label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} fullWidth />
              <TextField label="URL" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} fullWidth helperText="Endpoint HTTPS que recibir� POST JSON" />
              <TextField label="Descripci�n" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} fullWidth multiline minRows={2} />
              <TextField
                label={editing?.hasSecret ? 'Secret (dejar vac�o para mantener)' : 'Secret (HMAC)'}
                value={form.secret}
                onChange={(e) => setForm({ ...form, secret: e.target.value })}
                fullWidth
                type="password"
              />
              {editing?.hasSecret && (
                <FormControlLabel
                  control={<Checkbox checked={form.clearSecret} onChange={(e) => setForm({ ...form, clearSecret: e.target.checked })} />}
                  label="Eliminar secret"
                />
              )}
              <FormControlLabel
                control={<Switch checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />}
                label="Activo"
              />
            </Stack>
          )}

          {formTab === 1 && (
            <Stack spacing={1}>
              {eventsByGroup.map(([group, items]) => (
                <Accordion key={group}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>{group}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack>
                      {items.map((ev) => (
                        <FormControlLabel
                          key={ev.event}
                          control={
                            <Checkbox
                              checked={form.eventos.includes(ev.event)}
                              onChange={() => toggleEvent(ev.event)}
                            />
                          }
                          label={`${ev.event} � ${ev.label}${ev.emittable ? '' : ' (pr�ximamente)'}`}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}

          {formTab === 2 && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Match</InputLabel>
                <Select
                  label="Match"
                  value={form.filtrosMatch}
                  onChange={(e) => setForm({ ...form, filtrosMatch: e.target.value as 'all' | 'any' })}
                >
                  <MenuItem value="all">Todas las reglas (AND)</MenuItem>
                  <MenuItem value="any">Cualquier regla (OR)</MenuItem>
                </Select>
              </FormControl>
              {form.filtrosRules.map((rule, idx) => (
                <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl fullWidth>
                    <InputLabel>Campo</InputLabel>
                    <Select
                      label="Campo"
                      value={rule.field}
                      onChange={(e) => {
                        const next = [...form.filtrosRules];
                        next[idx] = { ...rule, field: e.target.value };
                        setForm({ ...form, filtrosRules: next });
                      }}
                    >
                      {filterFields.map((f) => (
                        <MenuItem key={f.field} value={f.field}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Operador</InputLabel>
                    <Select
                      label="Operador"
                      value={rule.operator}
                      onChange={(e) => {
                        const next = [...form.filtrosRules];
                        next[idx] = { ...rule, operator: e.target.value };
                        setForm({ ...form, filtrosRules: next });
                      }}
                    >
                      {(filterFields.find((f) => f.field === rule.field)?.operators
                        ?? ['equals', 'changed']).map((op) => (
                        <MenuItem key={op} value={op}>{op}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Valor"
                    fullWidth
                    value={rule.value == null ? '' : String(rule.value)}
                    onChange={(e) => {
                      const next = [...form.filtrosRules];
                      next[idx] = { ...rule, value: e.target.value };
                      setForm({ ...form, filtrosRules: next });
                    }}
                  />
                  <Button color="error" onClick={() => setForm({
                    ...form,
                    filtrosRules: form.filtrosRules.filter((_, i) => i !== idx),
                  })}>Quitar</Button>
                </Stack>
              ))}
              <Button
                onClick={() => setForm({
                  ...form,
                  filtrosRules: [
                    ...form.filtrosRules,
                    { field: filterFields[0]?.field ?? 'asentado', operator: 'equals', value: '' },
                  ],
                })}
              >
                Agregar regla
              </Button>
            </Stack>
          )}

          {formTab === 3 && (
            <Stack spacing={2}>
              <TextField
                type="number"
                label="M�x. reintentos"
                value={form.maxReintentos}
                onChange={(e) => setForm({ ...form, maxReintentos: Number(e.target.value) })}
              />
              <TextField
                type="number"
                label="Timeout (ms)"
                value={form.timeoutMs}
                onChange={(e) => setForm({ ...form, timeoutMs: Number(e.target.value) })}
              />
              <TextField
                label="Headers (JSON)"
                value={form.headersText}
                onChange={(e) => setForm({ ...form, headersText: e.target.value })}
                multiline
                minRows={3}
                fullWidth
                placeholder='{"Authorization":"Bearer ..."}'
              />
              <TextField
                label="Email alerta fallos"
                value={form.emailAlertaFallos}
                onChange={(e) => setForm({ ...form, emailAlertaFallos: e.target.value })}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
            {saving ? 'Guardando�' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={syncOpen} onClose={() => setSyncOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sincronizar clientes</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={syncOpts.soloActivos} onChange={(e) => setSyncOpts({ ...syncOpts, soloActivos: e.target.checked })} />}
              label="Solo activos"
            />
            <FormControlLabel
              control={<Checkbox checked={syncOpts.omitirYaSincronizados} onChange={(e) => setSyncOpts({ ...syncOpts, omitirYaSincronizados: e.target.checked })} />}
              label="Omitir ya sincronizados"
            />
            <FormControlLabel
              control={<Checkbox checked={syncOpts.dryRun} onChange={(e) => setSyncOpts({ ...syncOpts, dryRun: e.target.checked })} />}
              label="Dry-run (no env�a)"
            />
            {syncJob && (
              <>
                <Divider />
                <Typography variant="body2">Estado: {syncJob.status}</Typography>
                <Typography variant="body2">
                  Total {syncJob.total} � Enviados {syncJob.enviados} � Omitidos {syncJob.omitidos} � Fallidos {syncJob.fallidos}
                </Typography>
                {syncJob.errorMessage && <Alert severity="error">{syncJob.errorMessage}</Alert>}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {syncJob && !['completed', 'failed', 'cancelled', 'pending'].includes(syncJob.status) && (
            <Button color="warning" onClick={() => syncJob && void webhookService.cancelSyncJob(syncJob.id)}>Cancelar job</Button>
          )}
          <Button onClick={() => setSyncOpen(false)}>Cerrar</Button>
          <Button variant="contained" onClick={() => void startSync()}>Iniciar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Evita importar Tooltip si el �rbol ya es denso; wrapper m�nimo. */
const TooltipTitle: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
  <span title={title}>{children}</span>
);

export default Webhooks;
